import * as React from "react";
import { Provider } from "react-redux";

import analyseStore, { initialState } from "./store";
import AnalyseApp from "./AnalyseApp";
import { useParams } from "react-router-dom";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";

declare const window: Window;

interface AnalysisAppUrlParams {
    primarySetId: string;
    isViewOnly: string;
}

const escapeSpacebarForElementIds = [
    "zoomLevelSlider",
    "CurrentPosition",
    "EventType",
    "ToggleWaveform"
];

export const AnalyseAppProvider = () => {
    const { primarySetId, isViewOnly } = useParams<AnalysisAppUrlParams>();
    const [canEdit, setCanEdit] = React.useState<boolean>(false);
    const [ready, setReady] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();
    // Hijacks spacebar keydown events and makes sure for some specific scenarios (elements) it propagates over to the playback.
    React.useEffect(() => {
        const setup = async () => {
            setReady(false);
            const keyDownListener = (e: KeyboardEvent & { target: EventTarget & { id: string } }) => {
                if (
                    e.code === "Space" &&
                    escapeSpacebarForElementIds.indexOf(e.target.id) > -1
                ) {
                    e.preventDefault();
                    document.getElementById("Playback").click();
                }
            }

            const access = await legacyClient.getCoughAnalysisSetAccess(primarySetId);
            setCanEdit(access.CanEdit);
            window.addEventListener("keydown", keyDownListener);
            setReady(true);
            return () => window.removeEventListener("keydown", keyDownListener);
        }

        setup();
    }, []);

    if (!ready) {
        return (<LoadingWithMessage message="Loading Recording..." />);
    }

    const store = analyseStore(
        initialState(primarySetId, !canEdit, isViewOnly === "true")
    );

    return (
        <Provider store={store}>
            <AnalyseApp />
        </Provider>
    );
};
